import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { useTranslate } from "../../../../components/TranslationText";
import { AuthStorage } from "../../shared/utils/AuthStorage";
import {
    ServiceEventNames,
    ServiceActionTypes,
    PaymentStatus
} from "../../shared/utils/ServicesConstants";
import { IBC_SERVICE_DEFINITION_ID } from "../utils/InternationalBreedingCertificateConstants";

import { useInternationalBreedingCertificateData } from "./useInternationalBreedingCertificateData";
import { useIBCForm } from "./useIBCForm";
import { useIBCSubmission } from "./useIBCSubmission";

/**
 * Primary Controller Facade for International Breeding Certificate Service.
 * Composes data querying, form state lifecycle, and submission orchestration into a clean interface.
 */
export const useInternationalBreedingCertificate = () => {
    const { translate } = useTranslate();
    const location = useLocation();

    // URL parameters
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const id = searchParams.get("id");
    const action = searchParams.get("action");
    const serviceId = searchParams.get("serviceId") || IBC_SERVICE_DEFINITION_ID;

    // Session & Redux language
    const { currentLanguage } = useSelector((state) => state.studBookSlice || {});
    const initialOwnerId = AuthStorage.getInitialOwnerId();

    // 1. Data Layer: Fetch request, definition, lookups, and eligible certificates
    const {
        IBC_SERVICE_DEFINITION_ID: defaultServiceDefId,
        isLoading,
        eligibleCertificatesData,
        getServicesRequestByIdData,
        getServiceDefinitionByIdData,
        colors,
        countries,
        fetchEligibleCertificates
    } = useInternationalBreedingCertificateData(id, initialOwnerId, currentLanguage, action);

    // DTO extraction & Derived states
    const ibcRequestData =
        getServicesRequestByIdData?.internationalBreedingCertificateDto ||
        getServicesRequestByIdData?.coveringCertificates ||
        getServicesRequestByIdData;

    const activeEventName =
        getServicesRequestByIdData?.eventName || ibcRequestData?.eventName || "";
    const isSentBack = activeEventName.toLowerCase() === ServiceEventNames.SENT_BACK.toLowerCase();
    const isDraft = activeEventName.toLowerCase() === ServiceEventNames.DRAFT.toLowerCase();
    const isPaymentComplete = getServicesRequestByIdData?.paymentStatus === PaymentStatus.COMPLETE;
    const isReadOnly =
        action === ServiceActionTypes.VIEW ||
        (Boolean(id) && !isSentBack && !isDraft && action !== ServiceActionTypes.EDIT);

    const serviceFees = getServiceDefinitionByIdData?.serviceFees ?? 0;
    const hasFees = serviceFees > 0 && !isSentBack && !isPaymentComplete;
    const canParticipateWithoutPayment = AuthStorage.canParticipateWithoutPayment();
    const termsList = getServiceDefinitionByIdData?.termsList || [];

    // 2. Form State & Lifecycle Layer: Validation schema, hydration, and auto-population
    const { methods, selectedOwnerId, handleOwnerChange, onCertificateChange } = useIBCForm({
        id,
        serviceId,
        defaultServiceDefId,
        currentLanguage,
        termsList,
        isReadOnly,
        requestData: getServicesRequestByIdData,
        eligibleCertificatesData,
        fetchEligibleCertificates,
        translate
    });

    // 3. Submission & Payment Routing Layer: Async unwrap, validation scroll, payment redirection
    const { handleCreateEdit } = useIBCSubmission({
        methods,
        id,
        serviceId,
        defaultServiceDefId,
        selectedOwnerId,
        requestData: getServicesRequestByIdData,
        hasFees,
        currentLanguage,
        serviceDefinitionData: getServiceDefinitionByIdData
    });

    // 4. Declarative View Props passed to InternationalBreedingCertificateRequestPage
    return {
        methods,
        isLoading,
        serviceTitle:
            currentLanguage === "ar"
                ? getServiceDefinitionByIdData?.nameAr
                : getServiceDefinitionByIdData?.title,
        referenceNumber: ibcRequestData?.referenceNumber || "",
        showUserDropDown: !id || action === ServiceActionTypes.EDIT,
        selectedOwnerId,
        handleOwnerChange,
        eligibleCertificatesData,
        isReadOnly,
        onCertificateChange,
        colors,
        countries,
        commentsData: {
            comment: getServicesRequestByIdData?.comment || ibcRequestData?.comment,
            isSentBack,
            isReadOnly
        },
        termsList,
        actionButtonsProps: {
            id,
            action,
            eventName: activeEventName,
            serviceId,
            hasFees,
            canParticipateWithoutPayment,
            reportUrl: getServicesRequestByIdData?.reportUrl || ibcRequestData?.reportUrl,
            isSentBack,
            selectedOwnerId,
            handleCreateEdit
        }
    };
};
